export type ReplyBlock = { kind: 'heading' | 'paragraph' | 'list' | 'table' | 'coverage'; lines: string[] }

/** Opening a file must respect the visible selection, including bulk clear and folder changes. */
export function selectedPickerSource(kind: 'baseline' | 'favorite' | 'history', chosen: string, selectedBaselines: string[], visibleIds: string[]): string {
  const selected = kind === 'baseline' ? visibleIds.filter(id => selectedBaselines.includes(id)) : visibleIds.filter(id => id === chosen);
  return selected.includes(chosen) ? chosen : selected[0] ?? '';
}

/** Older answers used inline labels; normalize them without treating model output as HTML. */
export function replyBlocks(text: string): ReplyBlock[] {
  const normalized = text.replace(/\r\n/g, '\n')
    .replace(/(^|[。；;\n]|\s{2,})(依据|结论|待确认事项|建议|下一步)[：:]\s*/g, '$1\n\n### $2\n');
  const lines = normalized.split('\n'), blocks: ReplyBlock[] = [];
  for (let index = 0; index < lines.length; index++) {
    const line = lines[index]!.trim();
    if (!line) continue;
    if (/^本次读取\s*\d/.test(line)) { blocks.push({ kind: 'coverage', lines: [line] }); continue; }
    if (/^#{1,6}\s+/.test(line)) { blocks.push({ kind: 'heading', lines: [line.replace(/^#{1,6}\s+/, '')] }); continue; }
    if (/^\|.*\|$/.test(line) && /^\|?[\s:|\-]+\|?$/.test(lines[index + 1]?.trim() ?? '')) {
      const rows = [line]; index++;
      while (/^\|.*\|$/.test(lines[index + 1]?.trim() ?? '')) rows.push(lines[++index]!.trim());
      blocks.push({ kind: 'table', lines: rows }); continue;
    }
    const list = /^(?:[-*+]\s+|\d+[.)、]\s*)/;
    if (list.test(line)) {
      const items = [line.replace(list, '')];
      while (list.test(lines[index + 1]?.trim() ?? '')) items.push(lines[++index]!.trim().replace(list, ''));
      blocks.push({ kind: 'list', lines: items }); continue;
    }
    const previous = blocks.at(-1);
    if (previous?.kind === 'paragraph' && index > 0 && lines[index - 1]?.trim()) previous.lines.push(line);
    else blocks.push({ kind: 'paragraph', lines: [line] });
  }
  return blocks;
}

export function selectableMaterialIds(files: { id: string; parse_status: string; text: string }[]): string[] {
  return files.filter(file => file.parse_status === 'success' && file.text.trim()).map(file => file.id);
}

type TreeMaterial = { id:string; name:string; folder_id?:string|null; parse_status:string; text:string }
export type ProjectMaterialNode<T> = { id:string; name:string; files:T[]; folders:ProjectMaterialNode<T>[]; selectableIds:string[]; count:number }
/** Use the project's actual folder hierarchy; parent selection includes all readable descendants. */
export function projectMaterialTree<T extends TreeMaterial>(files:T[],folders:{id:string;name:string;parent_id?:string|null}[]):ProjectMaterialNode<T> {
  const known=new Set(folders.map(folder=>folder.id))
  const parent=(id?:string|null)=>id&&known.has(id)?id:null
  const build=(id:string|null,name:string,ancestors:string[]):ProjectMaterialNode<T>=>{
    const own=files.filter(file=>parent(file.folder_id)===id).sort((a,b)=>a.name.localeCompare(b.name,'zh-CN',{numeric:true}))
    const children=folders.filter(folder=>parent(folder.parent_id)===id&&!ancestors.includes(folder.id)).sort((a,b)=>a.name.localeCompare(b.name,'zh-CN',{numeric:true})).map(folder=>build(folder.id,folder.name,[...ancestors,folder.id]))
    return{id:id??'',name,files:own,folders:children,selectableIds:[...selectableMaterialIds(own),...children.flatMap(child=>child.selectableIds)],count:own.length+children.reduce((sum,child)=>sum+child.count,0)}
  }
  return build(null,'全部资料',[])
}

/** A single insertion position serves source copies and movement of existing pages. */
export function positionProjectPage<T extends { id: string }>(pages: T[], page: T, position: number): T[] {
  const from = pages.findIndex(item => item.id === page.id);
  const output = pages.filter(item => item.id !== page.id);
  const target = Math.max(0, Math.min(output.length, position - Number(from >= 0 && from < position)));
  output.splice(target, 0, page);
  return output;
}

export function projectDropPosition(pointerY: number, top: number, height: number, index: number): number {
  return index + Number(pointerY >= top + height / 2);
}
